import {
  useEffect,
  useState,
  forwardRef,
  useRef,
  useImperativeHandle
} from 'react'
import Typography from '@mui/material/Typography'
import ClockIcon from '../../../images/clockIcon.svg'
import {
  Box,
  Button,
  Dialog,
  FormControl,
  InputAdornment,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  useMediaQuery
} from '@mui/material'
import { useStyles } from '../style'
import { useParams } from 'react-router-dom'

import {
  availableTimeSlots,
  eventDetail,
  createSeries,
  createSeriesEvent,
  selectedSlotRoomId,
  bookTimeSlotsLive,
  updatePublicationDate,
  bookedSlotsPerDate,
  updateEventSeries
} from '../../../services/EventService'

import moment from 'moment'
import { appTheme } from '../../../theme'
import Slots from '../Slots'
import { Message } from '../../../components/Message/Message'
import { convertTimeIntoMinutes } from '../../../services/Helper'
import { useWebcast } from '../../../hooks/useWebcast'
import SpeakerAndStaffSupport from '../SpeakerAndStaffSupport'
import { MobileDatePickerComponent } from '../../../components/MobileDatePicker/MobileDatePickerComponent'

const ITEM_HEIGHT = 48
const ITEM_PADDING_TOP = 8
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 3.5 + ITEM_PADDING_TOP,
      width: 172
    }
  }
}

interface AddNewSeriesEventProps {
  setOpenAddEvent: any
  openAddEvent: boolean
  seriesFirstEventId: number
  updateSeriesEvents: any
  index: number
  eventType: string
}
const AddNewSeriesEvent = forwardRef(
  (
    {
      updateSeriesEvents,
      seriesFirstEventId,
      setOpenAddEvent,
      openAddEvent,
      index,
      eventType
    }: AddNewSeriesEventProps,
    ref: any
  ) => {
    useImperativeHandle(ref, () => ({
      updateEvent: (eventData: any) => {
        setEvent(eventData)
        setTitle('')
        setSummary('')
      }
    }))

    const fullScreen = useMediaQuery(appTheme.breakpoints.down('md'))
    const [openLiveEventSlot, setOpenLiveEventSlot] = useState(false)

    const classes = useStyles()

    // Time Picker logics end
    const [selectedLiveSlot, setLiveSlot] = useState<string>('')
    const [selectedDrySlot, setDrySlot] = useState<string>('')
    const [seriesId, setSeriesId] = useState(0)

    const clickLiveSlot = (slot: any) => {
      setErrorMessage(false)
      setLiveSlot(slot)
    }

    const [title, setTitle] = useState('')
    const [summary, setSummary] = useState('')

    let { id } = useParams()
    const [showErrorMessage, setErrorMessage] = useState(false)
    const [validationMessage, setValidationMessage] = useState('')
    const [showSuccessMessage, setShowSuccesMessage] = useState(false)
    const [successMessage, setSuccesMessage] = useState('')
    const [event, setEvent] = useState<any>({})
    const [audience, setAudience] = useState(0)
    const staffRef = useRef<any>(null)
    const [liveSlotsPerDate, setLiveSlotsPerDate] = useState({})

    // Live Event Run Logic start
    const [liveDuration, setLiveDuration] = useState(30)
    const [liveTimeSlots, setLiveTimeSlots] = useState<string[]>([])
    const [eventLiveDate, setEventLiveDate] = useState<Date | null>(
      () => new Date()
    )
    const { minimumLiveDate, getMinimumPublicationDate } = useWebcast()

    const [publicationDate, setPublicationDate] =
      useState<Date | null>(() => new Date())

    let minimumPublicationDate = getMinimumPublicationDate(eventLiveDate!, liveDuration)

    useEffect(() => {
      setPublicationDate(getMinimumPublicationDate(eventLiveDate!, liveDuration))
    }, [eventLiveDate, liveDuration])

    useEffect(() => {
      setPublicationDate(publicationDate!)
    }, [publicationDate!])

    // Event Dry Run Logic start
    const [dryDuration, setDryDuration] = useState(0)
    const [eventDryDate, setEventDryDate] = useState<Date | null>(
      () => new Date(eventLiveDate!)
    )

    const displayServerError = () => {
      setValidationMessage('Internal server error')
      showErrorPopup()
    }

    const [errors, setErrors] = useState({ title: '', summary: '' })
    const handleChange = (event: any) => {
      let name = event.target.id
      let value = event.target.value

      if (name === 'title') {
        if (value.length > 100) {
          setErrors({ ...errors, title: 'Character limit reached' })
        } else {
          setErrors({ ...errors, title: '' })
        }
        if (value.length <= 100) {
          setTitle(value)
        }
      }
      if (name === 'summary') {
        if (value.length > 500) {
          setErrors({ ...errors, summary: 'Character limit reached' })
        } else {
          setErrors({ ...errors, summary: '' })
        }
        if (value.length <= 500) {
          setSummary(value)
        }
      }
    }

    const loadEventData = async () => {
      try {
        const { data } = await eventDetail(Number(id))
        if (data) {
          if (data.eventDetail.statusCode === 200) {
            if (data.eventDetail.data === null) {
              //navigate('/')
            } else {
              //setEvent(data.eventDetail.data)
              setSeriesId(data.eventDetail.data.seriesId)
              setAudience(data.eventDetail.data.overHundredParticipants ? 3000 : 500)
            }
          }
        }
      } catch (error) {
        console.log(error)
      }
    }

    const loadTimeSlots = async (duration: number, category: string) => {
      const input: any = {
        duration: duration,
        liveBookingDate: moment(eventLiveDate).format('YYYY-MM-DD'),
        dryBookingDate: moment(eventDryDate).format('YYYY-MM-DD'),
        audience: audience,
        category: category
      }
      try {
        const { data } = await availableTimeSlots(0, input, eventType)
        if (data) {
          if (data.availableTimeSlots.statusCode === 200) {
            if (category === 'live') {
              setLiveTimeSlots(data.availableTimeSlots.data)
            }
          }
        }
      } catch (error) {
        console.log(error)
      }
    }

    const getTimeSlots = async (duration: number, category: string) => {
      const input: any = {
        duration: duration,
        liveBookingDate: moment(eventLiveDate).format('YYYY-MM-DD'),
        dryBookingDate: moment(eventDryDate).format('YYYY-MM-DD'),
        audience: audience,
        category: category
      }
      try {
        const { data } = await availableTimeSlots(0, input, eventType)
        if (data) {
          if (data.availableTimeSlots.statusCode === 200) {
            if (category === 'live') {
              setLiveTimeSlots(data.availableTimeSlots.data)
            }
          }
        }
      } catch (error) {
        displayServerError()
      }
    }

    const showErrorPopup = () => {
      setErrorMessage(true)
      setTimeout(() => {
        setErrorMessage(false)
      }, 3000)
    }

    const showSuccessPopup = () => {
      setShowSuccesMessage(true)
      setTimeout(() => {
        setShowSuccesMessage(false)
        handleEventClose()
      }, 3000)
    }

    const getNextAvailableDate = (date: Date | null, duration: number) => {
      if (!date) return null
      const blockedDates = getBlockedDates(duration)
  
      let minimumDate = moment(minimumLiveDate)
      let currentDate = moment(date)
      if (currentDate.isBefore(minimumDate)) currentDate = minimumDate
      while (true) {
        if (!blockedDates.includes(currentDate.format('YYYY-MM-DD'))) break
        currentDate = currentDate.add(currentDate.day() === 5 ? 3 : 1, 'day')
      }
  
      return currentDate.toDate()
    }

    const handleLiveDuration = async (event: SelectChangeEvent) => {
      if (+event.target.value === liveDuration) return

      setErrorMessage(false)
      setLiveDuration(parseInt(event.target.value))
      setEventLiveDate(getNextAvailableDate(eventLiveDate, +event.target.value))
      await getTimeSlots(Number(event.target.value), 'live')
    }

    const loadBookedSlotsPerDate = async (category: string) => {
      try {
        const { data } = await bookedSlotsPerDate(0, { audience, category }, 'Webcast')
  
        if (data.bookedSlotsPerDate.statusCode === 200) {
          setLiveSlotsPerDate(data.bookedSlotsPerDate.data)
        }
      } catch (error) {
        console.log(error)
      }
    }

    useEffect(() => {
      if (id !== undefined && openAddEvent) {
        loadEventData()
      }
      setErrors({ ...errors, title: '', summary: '' })
    }, [openAddEvent])

    useEffect(() => {
      loadTimeSlots(liveDuration, 'live')
      loadBookedSlotsPerDate('live')
    }, [audience, liveDuration])

    useEffect(() => (
      setEventLiveDate(getNextAvailableDate(eventLiveDate, liveDuration))
    ), [liveSlotsPerDate])

    useEffect(() => {
      getTimeSlots(Number(liveDuration), 'live')
      setLiveSlot('')
    }, [eventLiveDate])

    const getSelectedSlotRoomId = async (
      selectedSlot: string,
      duration: number,
      category: string
    ) => {
      const input: any = {
        duration: duration,
        liveBookingDate: moment(eventLiveDate).format('YYYY-MM-DD'),
        dryBookingDate: moment(eventDryDate).format('YYYY-MM-DD'),
        audience: audience,
        category: category
      }
      let slotRoomId: any = 0

      try {
        let currentEventId = 0
        const { data } = await selectedSlotRoomId(
          selectedSlot,
          currentEventId,
          eventType,
          input
        )
        if (data) {
          if (data.selectedSlotRoomId.statusCode === 200) {
            if (category === 'live') {
              slotRoomId = data.selectedSlotRoomId.data.slotRoomId
            }
          }
        }
      } catch (error) {
        console.log(error)
      }
      return slotRoomId
    }

    const handleEventClose = () => {
      updateSeriesEvents()
      setOpenAddEvent(false)

      setEventLiveDate(new Date())
      setLiveDuration(30)
      setLiveSlot('')
      setDryDuration(0)
      setDrySlot('')
    }

    const createNewSeriesEvent = async (sId: number) => {
      let eventId = 0
      try {
        const results = await createSeriesEvent(title, summary, eventType, sId)
        const { data } = results
        if (data) {
          if (data.createSeriesEvent.statusCode === 200) {
            eventId = data.createSeriesEvent.data.id
          } else {
            console.log(data.createSeriesEvent.message)
          }
        } else {
          console.log('Server error')
        }
      } catch (error) {
        console.log(error)
      }
      return eventId
    }

    const createEventSeries = async (eventId: number) => {
      let seriesId = 0
      try {
        const results = await createSeries(eventId)
        const { data } = results
        if (data) {
          if (data.createSeries.statusCode === 200) {
            seriesId = data.createSeries.data.seriesId
          } else {
            console.log(data.createSeries.message)
          }
        } else {
          console.log('Server error')
        }
      } catch (error) {
        console.log(error)
      }
      return seriesId
    }

    const eventPublicationDate = async (eventId: number) => {
      const input: any = {
        publicationDate: moment(publicationDate)
          .set({ hour: 0, minute: 0, second: 0 })
          .format('YYYY-MM-DD')
      }
      try {
        const results = await updatePublicationDate(eventId, input)
        const { data } = results
        if (data) {
          if (data.updatePublicationDate.statusCode === 200) {
            //navigate('/event/staff/'+eventData.id);
          }
        }
      } catch (error) {
        //displayServerError()
      }
    }

    const nextClick = async () => {
      let liveStartTime = convertTimeIntoMinutes(selectedLiveSlot)
      let dryStartTime = convertTimeIntoMinutes(selectedDrySlot)

      if (title === '') {
        setValidationMessage('Please enter event title')
        showErrorPopup()
        setErrors({ ...errors, title: 'Please enter event title' })
        return
      }

      if (summary === '') {
        setValidationMessage('Please enter event summary')
        showErrorPopup()
        setErrors({ ...errors, summary: 'Please enter event summary' })
        return
      }

      if (liveDuration === 0) {
        setValidationMessage('Please select live duration')
        showErrorPopup()
        return
      }
      if (liveStartTime === 0) {
        setValidationMessage('Please select live start time')
        showErrorPopup()
        return
      }
      const input: any = {
        liveDuration: liveDuration,
        liveBookingDate: moment(eventLiveDate)
          .set({ hour: 0, minute: 0, second: 0 })
          .format('YYYY-MM-DD'),
        liveStartTime: liveStartTime,
        dryDuration: dryDuration,
        dryBookingDate: moment(eventDryDate)
          .set({ hour: 0, minute: 0, second: 0 })
          .format('YYYY-MM-DD'),
        dryStartTime: dryStartTime,
        audience: audience
      }

      try {
        let dryRoomId = 0
        let liveRoomId = await getSelectedSlotRoomId(
          selectedLiveSlot,
          liveDuration,
          'live'
        )
        if (liveRoomId === 0) {
          setValidationMessage('Live Event room not available')
          showErrorPopup()
          return
        }

        let sId = seriesId
        if (seriesId === 0) {
          sId = await createEventSeries(Number(seriesFirstEventId))
          setSeriesId(sId)
          await updateEventSeries(Number(id), sId)
        }

        let staffValidateMessage = await staffRef.current.validateNewStaff()
        if (staffValidateMessage !== '') {
          setValidationMessage(staffValidateMessage)
          showErrorPopup()
          return
        }

        let eventId = await createNewSeriesEvent(sId)

        let staffCreateMessage = await staffRef.current.createNewStaff(eventId)
        if (staffCreateMessage !== '') {
          setValidationMessage(staffCreateMessage)
          showErrorPopup()
          return
        }

        const { data } = await bookTimeSlotsLive(
          liveRoomId,
          dryRoomId,
          eventId,
          input
        )
        if (data) {
          if (data.bookTimeSlotsLive.statusCode === 200) {
            await eventPublicationDate(eventId)
            setSuccesMessage('new series event created successfuly')
            showSuccessPopup()
            //navigate('/featuresAndRequests/' + event.id)
          } else if (data.bookTimeSlotsLive.statusCode === 404) {
            setValidationMessage(data.bookTimeSlotsLive.message)
            showErrorPopup()
          } else {
            console.log(data.bookTimeSlotsLive.message)
            setValidationMessage('Internal server error')
            showErrorPopup()
          }
        } else {
          setValidationMessage('Internal server error')
          showErrorPopup()
        }
      } catch (error) {
        console.log(error)
      }
    }

    const getBlockedDates = (duration?: number) => {
      let blockedDates: string[] = []
  
      Object.entries(liveSlotsPerDate).forEach((x: any) => {
        if (!x[1][duration || liveDuration]) blockedDates.push(x[0])
      })
  
      return blockedDates
    }

    return (
      <Dialog
        fullScreen={fullScreen}
        open={openAddEvent}
        onClose={handleEventClose}
      >
        <Box
          sx={{
            width: '667px',
            borderRadius: '12px !important',
            px: '32px',
            py: '20px',
            gap: '24px',
            background: '#FFFFFF',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          {showErrorMessage && (
            <Message message={validationMessage} isError={true} />
          )}
          {showSuccessMessage && (
            <Message message={successMessage} isSuccess={true} />
          )}

          <Box sx={{ display: 'flex', flexDirection: 'row' }}>
            <Box
              sx={{
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                border: '1px solid #E6E1E5',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginRight: '50px'
              }}
            >
              <Typography
                color='#79747E'
                fontSize='16px !important'
                sx={{ lineHight: '24px' }}
              >
                {index}
              </Typography>
            </Box>
            <Typography
              sx={{
                fontSize: '23px !important',
                fontWeight: '700',
                lineHeight: '32px'
              }}
            >
              Series event info
            </Typography>
          </Box>

          <div>
            <TextField
              fullWidth
              id='title'
              label='Title'
              value={title}
              onChangeCapture={handleChange}
              required
              InputLabelProps={{ shrink: true }}
              placeholder='Title'
              error={errors.title !== '' ? true : false}
              // onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              //   setTitle(event.target.value)
              // }}
              sx={{
                '.MuiOutlinedInput-root.Mui-focused': {
                  fieldset: { borderColor: '#006BD7' }
                },
                [`& fieldset`]: {
                  borderRadius: '8px'
                }
              }}
            />
            <Box sx={{ display: 'flex' }}>
              <Typography
                sx={{
                  color: '#D50000',
                  flex: 1,
                  fontSize: 12,
                  ml: 1.5,
                  textAlign: 'left'
                }}
              >
                {errors.title}
              </Typography>
              <Typography sx={{ color: 'rgba(0, 0, 0, 0.6)', fontSize: 12 }}>
                {title.length} / 100
              </Typography>
            </Box>
          </div>

          <div>
            <TextField
              fullWidth
              id='summary'
              label='Summary'
              multiline
              rows={4}
              value={summary}
              onChangeCapture={handleChange}
              required
              InputLabelProps={{ shrink: true }}
              placeholder='Summary'
              error={errors.summary !== '' ? true : false}
              // onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              //   setSummary(event.target.value)
              // }}
              sx={{
                '.MuiOutlinedInput-root.Mui-focused': {
                  fieldset: { borderColor: '#006BD7' }
                },
                [`& fieldset`]: {
                  borderRadius: '8px'
                }
              }}
            />
            <Box sx={{ display: 'flex' }}>
              <Typography
                sx={{
                  color: '#D50000',
                  flex: 1,
                  fontSize: 12,
                  ml: 1.5,
                  textAlign: 'left'
                }}
              >
                {errors.summary}
              </Typography>
              <Typography sx={{ color: 'rgba(0, 0, 0, 0.6)', fontSize: 12 }}>
                {summary.length} / 500
              </Typography>
            </Box>
          </div>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between'
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography
                sx={{
                  fontSize: '23px !important',
                  fontWeight: '700',
                  lineHeight: '32px'
                }}
              >
                Event Recording Date
              </Typography>
              <Box className={classes.datepickerWrapper} sx={{ mt: '10px' }}>
                <MobileDatePickerComponent
                  minimumDate={minimumLiveDate}
                  eventDate={eventLiveDate!}
                  setEventDate={setEventLiveDate}
                  blockedDates={getBlockedDates()}
                />
              </Box>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography
                sx={{
                  fontSize: '23px !important',
                  fontWeight: '700',
                  lineHeight: '32px'
                }}
              >
                Event Recording Time
              </Typography>
              <FormControl
                sx={{
                  minWidth: 225,
                  width: '100%',
                  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline':
                    {
                      borderColor: '#006BD7'
                    }
                  // '& .MuiOutlinedInput-notchedOutline': {
                  //   borderRadius: '8px'
                  // }
                }}
                size='small'
              >
                <Select
                  value={liveDuration.toString()}
                  onChange={handleLiveDuration}
                  MenuProps={MenuProps}
                  sx={{ textAlign: 'left' }}
                >
                  <MenuItem value={0} disabled>
                    Select Duration
                  </MenuItem>
                  <MenuItem value={30}>30 Minutes</MenuItem>
                  <MenuItem value={60}>60 Minutes</MenuItem>
                  <MenuItem value={90}>90 Minutes</MenuItem>
                </Select>
              </FormControl>

              <TextField
                fullWidth
                label='Time'
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>
                      <Box component='img' src={ClockIcon} alt='Clock Icon' />
                    </InputAdornment>
                  ),
                  style: {
                    height: '40px',
                    width: '100%'
                  }
                }}
                onClick={() => {
                  setOpenLiveEventSlot(true)
                }}
                value={selectedLiveSlot}
                sx={{
                  '.MuiOutlinedInput-root.Mui-focused': {
                    fieldset: { borderColor: '#006BD7' }
                  },
                  [`& fieldset`]: {
                    borderRadius: '4px'
                  }
                }}
              />
              <Slots
                open={openLiveEventSlot}
                setOpen={setOpenLiveEventSlot}
                selectedSlot={selectedLiveSlot}
                availableSlots={liveTimeSlots}
                setSlot={clickLiveSlot}
              />
            </Box>
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between'
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography
                sx={{
                  fontSize: '23px !important',
                  fontWeight: '700',
                  lineHeight: '32px'
                }}
              >
                Event Publication Date
              </Typography>
              <Box
                className={classes.datepickerWrapper}
                sx={{ mt: '10px', mb: '40px' }}
              >
                <MobileDatePickerComponent
                  minimumDate={minimumPublicationDate}
                  eventDate={publicationDate!}
                  setEventDate={setPublicationDate}
                />
              </Box>
            </Box>
          </Box>

          <SpeakerAndStaffSupport ref={staffRef}></SpeakerAndStaffSupport>

          <Box
            sx={{
              gap: '15px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center'
            }}
          >
            <Button
              onClick={nextClick}
              sx={{ fontSize: '14px !important', width: '65%' }}
              style={{
                backgroundColor: '#333333',
                borderRadius: '25px',
                height: '40px',
                color: '#FFFFFF',
                fontWeight: '500',
                lineHeight: '20px',
                textTransform: 'none'
              }}
            >
              Save
            </Button>

            <Button
              fullWidth
              variant='outlined'
              sx={{ fontSize: '14px !important', width: '65%' }}
              style={{
                borderRadius: '25px',
                height: '40px',
                fontWeight: '500',
                lineHeight: '20px',
                textTransform: 'none',

                border: '2px solid #333333'
              }}
              onClick={handleEventClose}
            >
              Exit
            </Button>
          </Box>
        </Box>
      </Dialog>
    )
  }
)
export default AddNewSeriesEvent
